/* AXOLOVE: choosing an option moves the gallery to a photo.
 *
 * The theme's own variant-to-media link re-sorts the gallery so the selected
 * variant's photo goes first. We want the opposite: the photo order stays as we
 * set it, and choosing an option only slides the gallery to a photo. So the
 * variants carry no media, and this script picks the slide.
 *
 * Two ways to say which photo, chosen by the page:
 *
 *  1. Map (Foot Lift Brace). An element with data-ax-gallery-map holds JSON
 *     from option name to slide: {"Foot side": 4, "Size": 1, "Color": "last"}.
 *     Numbers count from 1; "last" is the last photo. Clicking any value of that
 *     option, even the one already selected, goes to its photo. A value can also
 *     be an object to pick by the clicked value: {"Set": {"One — gray": "last"}}.
 *  2. Colour in the file name (No-Tie Laces). With no map, the slide whose image
 *     file name ends in "-<colour>" is used, e.g. "...-x3-black.png".
 *
 * The gallery slides at once on the click. The theme then replaces the whole
 * <media-gallery> (it has to ask the server first), and the new one starts on
 * its first slide. To keep that from flashing, the new gallery is hidden until
 * it has been put on the slide the shopper is looking at, then shown.
 */
(function () {
  'use strict';

  var TIMEOUT = 3000;

  function slug(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function galleryMap() {
    var holder = document.querySelector('[data-ax-gallery-map]');
    if (!holder) return null;
    try {
      return JSON.parse(holder.getAttribute('data-ax-gallery-map'));
    } catch (e) {
      return null;
    }
  }

  function checkedColour() {
    var picker = document.querySelector('variant-picker');
    var input = picker && picker.querySelector('input[type="radio"]:checked');
    return input ? slug(input.value) : '';
  }

  function slideshowOf(gallery) {
    return gallery && gallery.querySelector('slideshow-component');
  }

  function isReady(slideshow) {
    return !!(slideshow && typeof slideshow.select === 'function' && slideshow.slides && slideshow.slides.length && slideshow.refs && slideshow.refs.scroller);
  }

  function slideIndexForColour(colour, slideshow) {
    var slides = slideshow.slides || [];
    var pattern = new RegExp('-' + colour + '\\.[a-z]+(\\?|$)', 'i');
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      var src = img ? decodeURIComponent(img.currentSrc || img.src || '') : '';
      if (pattern.test(src)) return i;
    }
    return -1;
  }

  function slideIndexFromMap(mapValue, slideshow) {
    var count = (slideshow.slides || []).length;
    if (mapValue === 'last') return count - 1;
    var n = Number(mapValue);
    if (!n || n < 1 || n > count) return -1;
    return n - 1;
  }

  function currentIndex(slideshow) {
    return slideshow.current || 0;
  }

  /* After the theme swaps the gallery it finishes setting the new one up a few
     frames later (it queues a requestAnimationFrame, then a timeout, then
     another requestAnimationFrame that selects the first slide). Landing on the
     right slide once is not enough: that late step can undo it. So the new
     gallery stays hidden until the slide has held for STABLE ms, and we keep
     putting it back for KEEP ms after the swap. */
  var MIN_HIDDEN = 120;
  var STABLE = 80;
  var KEEP = 700;

  function move(fromIndex, oldGallery, wanted) {
    var started = Date.now();
    var replacedAt = 0;
    var stableSince = 0;
    var hidden = null;
    var done = false;
    var observer = null;

    function reveal() {
      if (hidden) hidden.style.opacity = '';
      hidden = null;
      if (observer) observer.disconnect();
      observer = null;
    }

    /* The theme swaps the gallery in a microtask. Watching the parent hides the
       new one in the same microtask checkpoint, so it is never painted on its
       first slide. */
    var holder = oldGallery && oldGallery.parentNode;
    if (holder && fromIndex > 0) {
      observer = new MutationObserver(function () {
        var gallery = document.querySelector('media-gallery');
        if (gallery && gallery !== oldGallery && !hidden) {
          gallery.style.opacity = '0';
          hidden = gallery;
        }
      });
      observer.observe(holder, { childList: true });
    }

    function tick() {
      if (done) return;

      var now = Date.now();
      var gallery = document.querySelector('media-gallery');
      var slideshow = slideshowOf(gallery);
      var replaced = !oldGallery || gallery !== oldGallery;

      if (replaced && !replacedAt) replacedAt = now;

      if (replaced && gallery && fromIndex > 0 && !hidden) {
        gallery.style.opacity = '0';
        hidden = gallery;
      }

      if (replaced && isReady(slideshow)) {
        var want = fromIndex > 0 ? fromIndex : wanted(slideshow);

        if (want >= 0 && currentIndex(slideshow) !== want) {
          slideshow.select(want, undefined, { animate: false });
          stableSince = 0;
        } else if (!stableSince) {
          stableSince = now;
        }

        if (hidden && stableSince && now - stableSince >= STABLE && now - replacedAt >= MIN_HIDDEN) reveal();

        if (!hidden && now - replacedAt >= KEEP) {
          done = true;
          return;
        }
      }

      if (now - started > TIMEOUT) {
        done = true;
        reveal();
        return;
      }

      schedule();
    }

    /* rAF runs before the next paint, which is what keeps the first slide from
       showing; the timer is only a fallback for a tab that is not drawing. */
    function schedule() {
      requestAnimationFrame(tick);
      setTimeout(tick, 30);
    }

    schedule();
  }

  /* The best fix for the flash is not to let the theme swap the gallery at all.
     Our variants carry no media, so the gallery the server sends back is the
     same one already on screen. The theme's media-gallery waits on the event's
     promise and then calls replaceWith(); we register first (document, capture
     phase), so our .then runs before it and drops the new gallery from the
     response. The theme finds nothing to swap and the one on screen just slides.
     If the photos ever differ (a variant that does carry media), we leave the
     response alone and the hide-and-restore path below takes over. */
  function photoKey(gallery) {
    var imgs = gallery ? gallery.querySelectorAll('slideshow-slide img') : [];
    var keys = [];
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('src') || imgs[i].getAttribute('data-src') || '';
      keys.push(src.split('?')[0]);
    }
    return keys.join('|');
  }

  document.addEventListener(
    'shopify:product:select',
    function (event) {
      if (!event.promise || !(event.target instanceof Element)) return;
      if (event.target.closest('product-card, quick-add-dialog')) return;
      var current = document.querySelector('media-gallery');
      if (!current) return;
      var before = photoKey(current);
      if (!before) return;
      event.promise
        .then(function (result) {
          var html = result && result.detail && result.detail.html;
          var fresh = html && html.querySelector && html.querySelector('media-gallery');
          if (fresh && fresh.parentNode && photoKey(fresh) === before) {
            fresh.parentNode.removeChild(fresh);
          }
        })
        .catch(function () {});
    },
    true
  );

  function handle(event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.closest('variant-picker')) return;

    var map = galleryMap();
    var gallery = document.querySelector('media-gallery');
    var slideshow = slideshowOf(gallery);
    var from = isReady(slideshow) ? currentIndex(slideshow) : 0;

    var wanted;
    if (map) {
      var optionName = target.getAttribute('data-option-name');
      if (!optionName || !Object.prototype.hasOwnProperty.call(map, optionName)) return;
      var rule = map[optionName];
      /* An object picks the photo by the value that was clicked; a value it
         does not name leaves the gallery where it is. */
      if (rule && typeof rule === 'object') rule = rule[target.value];
      if (rule === undefined || rule === null) return;
      wanted = function (ss) {
        return slideIndexFromMap(rule, ss);
      };
    } else {
      var colour = checkedColour();
      if (!colour) return;
      wanted = function (ss) {
        return slideIndexForColour(colour, ss);
      };
    }

    /* Slide straight away on the gallery that is on screen. */
    if (isReady(slideshow)) {
      var index = wanted(slideshow);
      if (index >= 0 && index !== from) {
        /* The theme animates a jump of more than one slide by lifting the target
           slide out of the DOM, parking it next to the current one, and putting
           it back afterwards. Re-inserting the <img> makes Chrome decode it again
           and the gallery goes blank for a moment, after the slide has arrived.
           So only a neighbouring slide animates; a longer jump is instant. */
        slideshow.select(index, undefined, { animate: Math.abs(index - from) <= 1 });
        from = index;
      }
    }

    /* When the theme's replacement lands, put it on the same slide. */
    move(from, gallery, wanted);
  }

  /* Click, not change: clicking the value that is already selected (the default
     "Left", say) sends no change event but should still move the gallery. */
  document.addEventListener('click', handle);

  /* A link that already names a variant (?variant=…) opens on its colour.
     Only for the colour-in-file-name mode; the map mode never moves on load. */
  if (!document.querySelector('[data-ax-gallery-map]') && /[?&]variant=/.test(location.search)) {
    var start = function () {
      var colour = checkedColour();
      if (!colour) return;
      move(0, null, function (ss) {
        return slideIndexForColour(colour, ss);
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
})();
